import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { addSentryBreadcrumb, captureException } from '../utils/sentry';
import { safeGetItem, safeSetItem } from '../utils/storage';

/**
 * API Configuration
 * Centralized axios instance with interceptors for auth and tenant scoping
 */

// Environment variables
// Prefer explicit VITE_API_BASE_URL / VITE_API_URL, otherwise use the
// same-origin API host to avoid cross-origin header/cookie issues.
// Setting this to https://api.clubware.app makes requests same-site from
// the app (https://app.clubware.app) and prevents Authorization/cookie
// stripping that can happen when calling a different origin.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  'https://api.clubware.app';

const AUTH_HEADER_NAME =
  (import.meta.env.VITE_API_AUTH_HEADER as string) || 'Authorization';

// The backend expects the tenant header to be `X-Tenant-ID` by convention.
// Allow overriding via VITE_API_TENANT_HEADER, but default to the expected name.
const TENANT_HEADER_NAME =
  (import.meta.env.VITE_API_TENANT_HEADER as string) || 'X-Tenant-ID';

// Default timeout
const DEFAULT_TIMEOUT = 30000; // 30 seconds

// Auth token management
let authToken: string | null = null;
let tenantId: string | null = null;

/**
 * Set the authentication token for API requests
 */
export function setAuthToken(token: string | null): void {
  if (!token) {
    authToken = null;
    return;
  }

  // Normalize token: if the token already contains a Bearer prefix, strip it
  // so the request interceptor can consistently prepend `Bearer ` once.
  const normalized = String(token).trim().replace(/^Bearer\s+/i, '');
  authToken = normalized;
}

/**
 * Clear the authentication token
 */
export function clearAuthToken(): void {
  authToken = null;
}

/**
 * Get the current authentication token
 */
export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Set the tenant ID for API requests
 */
export function setTenantId(id: string | null): void {
  tenantId = id;
}

/**
 * Clear the tenant ID
 */
export function clearTenantId(): void {
  tenantId = null;
}

/**
 * Get the current tenant ID
 */
export function getTenantId(): string | null {
  return tenantId;
}

/**
 * Create configured axios instance
 */
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Alias for backwards compatibility
 */
export const apiClient = api;

/**
 * Request interceptor to attach auth token and tenant headers
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Attempt to restore token from localStorage if missing from memory
    if (!authToken) {
      const stored = safeGetItem('cw_access_token');
      if (stored) {
        console.log('[API] Token missing from memory, restoring from localStorage');
        setAuthToken(stored);
      }
    }
    
    // Attach authentication token (be robust to header casing)
    if (authToken && config.headers) {
      const headerKey = AUTH_HEADER_NAME || 'Authorization';

      // If headerKey indicates the standard Authorization header, ensure we set it
      // in a way that works regardless of how axios stores header keys (lowercase/upper).
      if (headerKey.toLowerCase() === 'authorization') {
        const hasAuthHeader = !!(config.headers['Authorization'] || (config.headers as any)['authorization']);
        if (!hasAuthHeader) {
          // Set both forms to be safe for different axios versions/servers
          config.headers['Authorization'] = `Bearer ${authToken}`;
          (config.headers as any)['authorization'] = `Bearer ${authToken}`;
          console.log(`[API] Adding Authorization header to ${config.method?.toUpperCase()} ${config.url}`);
          // Masked preview for debugging (do NOT print full tokens in production logs)
          try {
            const masked = authToken ? `${String(authToken).slice(0, 6)}...(${String(authToken).length})` : null;
            console.log('[API] Auth header preview (masked):', masked);
            console.log('[API] Header values preview:', {
              Authorization: config.headers['Authorization'],
              authorization: (config.headers as any)['authorization'],
            });
          } catch (e) {
            // ignore logging errors
          }
        }
      } else {
        // Custom header name (non-standard). Set both exact and lowercase key.
        const hasCustom = !!(config.headers[headerKey] || (config.headers as any)[headerKey.toLowerCase()]);
        if (!hasCustom) {
          config.headers[headerKey] = authToken;
          (config.headers as any)[headerKey.toLowerCase()] = authToken;
          console.log(`[API] Adding ${headerKey} header to ${config.method?.toUpperCase()} ${config.url}`);
          try {
            const masked = authToken ? `${String(authToken).slice(0, 6)}...(${String(authToken).length})` : null;
            console.log('[API] Custom auth header preview (masked):', masked);
            console.log('[API] Header values preview:', {
              [headerKey]: config.headers[headerKey],
              [headerKey.toLowerCase()]: (config.headers as any)[headerKey.toLowerCase()],
            });
          } catch (e) {
            // ignore
          }
        }
      }
    } else if (!authToken && config.url && !config.url.includes('/auth/google')) {
      console.warn(`[API] No auth token available for ${config.method?.toUpperCase()} ${config.url}`);
    }

    // Attach tenant ID header
    // Attach tenant ID for tenant-scoped requests.
    // To avoid triggering CORS preflight issues that can remove Authorization
    // headers in some environments, prefer using a query param for cross-origin
    // requests and only set the tenant header for same-origin requests.
    if (tenantId) {
      try {
        const requestUrl = new URL(config.url || '', config.baseURL || window.location.origin);
        const isSameOrigin = requestUrl.origin === window.location.origin;

        // If same-origin, set the header (both casing forms)
        if (isSameOrigin && config.headers) {
          const lower = TENANT_HEADER_NAME.toLowerCase();
          if (!config.headers[TENANT_HEADER_NAME] && !(config.headers as any)[lower]) {
            config.headers[TENANT_HEADER_NAME] = tenantId;
            (config.headers as any)[lower] = tenantId;
          }
        } else {
          // Cross-origin: append tenant as query param to avoid preflight
          const paramName = (import.meta.env.VITE_API_TENANT_QUERY_PARAM as string) || 'tenant_id';
          config.params = config.params || {};
          if (!config.params[paramName]) {
            config.params[paramName] = tenantId;
          }
        }
      } catch (e) {
        // Fallback: if URL parsing fails, set both header and query param to be safe
        config.params = config.params || {};
        config.params['tenant_id'] = tenantId;
        if (config.headers) {
          config.headers[TENANT_HEADER_NAME] = tenantId;
          (config.headers as any)[TENANT_HEADER_NAME.toLowerCase()] = tenantId;
        }
      }
    }
    
    // Add Sentry breadcrumb for API request
    addSentryBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      'http',
      'info',
      {
        method: config.method,
        url: config.url,
        tenant_id: tenantId || 'none',
        has_auth_token: !!authToken,
      }
    );

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    captureException(error, { context: 'api_request_interceptor' });
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for Sentry breadcrumbs and error tracking
 */
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Add Sentry breadcrumb for successful response
    addSentryBreadcrumb(
      `API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      'http',
      'info',
      {
        method: response.config.method,
        url: response.config.url,
        status: response.status,
      }
    );
    return response;
  },
  (error: AxiosError) => {
    // Add Sentry breadcrumb for error response
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method;
    
    addSentryBreadcrumb(
      `API Error: ${method?.toUpperCase()} ${url} - ${status || 'Network Error'}`,
      'http',
      'error',
      {
        method,
        url,
        status,
        error: error.message,
      }
    );
    
    // Capture error in Sentry (but not for expected errors like 401, 403)
    if (status && status >= 500) {
      captureException(error, {
        api_method: method,
        api_url: url,
        status_code: status,
      });
    }
    
  const originalRequest = error.config as InternalAxiosRequestConfig & { __isRetryAttempt?: boolean };

    // If 401 and not already retried, attempt a refresh using the refresh cookie.
    if (status === 401 && originalRequest && !originalRequest.__isRetryAttempt) {
      console.log('[API] Got 401, attempting refresh and retry for:', originalRequest.url);
      originalRequest.__isRetryAttempt = true;

      // Try to refresh session using cookie-based refresh token.
      return axios
        .post(
          `${API_BASE_URL}/api/auth/refresh`,
          {},
          { 
            withCredentials: true, 
            headers: { 
              'Content-Type': 'application/json',
            } 
          }
        )
        .then((resp) => {
          try {
            const data = resp.data as any;
            if (data && data.access_token) {
              // Update local auth token for future requests
              const newToken = data.access_token as string;
              console.log('[API] Refresh successful, got new token:', `${newToken.slice(0, 6)}...(${newToken.length})`);
              setAuthToken(newToken);
              
              // Update localStorage if used by AuthContext
              const stored = safeSetItem('cw_access_token', newToken);
              if (!stored) {
                console.warn('[API] Failed to persist refreshed token to localStorage');
              }

              // IMPORTANT: Clear any existing auth headers from original request
              // to ensure we use the new token from the interceptor
              if (originalRequest.headers) {
                delete originalRequest.headers['Authorization'];
                delete (originalRequest.headers as any)['authorization'];
              }
              
              console.log('[API] Retrying original request:', originalRequest.url);
              // Retry - the request interceptor will add the new token
              return api.request(originalRequest);
            }
          } catch (e) {
            console.error('[API] Error processing refresh response:', e);
            captureException(e as Error, { context: 'api_refresh_processing' });
            // Fall through to reject
          }

          return Promise.reject(error);
        })
        .catch((refreshError) => {
          console.warn('[API] Refresh failed:', refreshError?.response?.status || refreshError?.message);
          return Promise.reject(error);
        });
    }

    return Promise.reject(error);
  }
);

/**
 * Helper to extract data from axios response
 */
export function handleResponse<T>(response: { data: T }): T {
  return response.data;
}

/**
 * Create a custom axios instance with different base URL or config
 */
export function createApiInstance(baseURL?: string, timeout?: number): AxiosInstance {
  return axios.create({
    baseURL: baseURL || API_BASE_URL,
    timeout: timeout || DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}
